import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Upload, X } from "lucide-react";
import { ContainerLink, ContainerLinks, HexColor, type Area, type Media, type Project, type Space } from "@desk/contracts";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, Label, Textarea } from "@/components/ui/input";
import { useMediaUrl } from "@/components/container-details";
import { ContainerGlyph } from "@/components/container-icon";
import { IconDialog } from "./IconDialog";
import { Tooltip } from "@/components/ui/misc";
import { logoColor } from "@/lib/logo-color";
import { cn } from "@/lib/utils";
import type { ApiClient } from "@/data/api";
import { useContainerOps } from "./ContainerActions";

const SWATCHES = ["#C41E24", "#E96B1A", "#D99A06", "#2F9E5B", "#138A8A", "#2870ED", "#5B5BD6", "#8E4EC6", "#D6409F", "#70757D"];
const MAX_UPLOAD = 12 * 1024 * 1024;

type Target = { kind: "area"; area: Area } | { kind: "space"; space: Space } | { kind: "project"; project: Project };

/** Dominant non-neutral color of an imported icon, sampled from the server's light PNG. Only ever a suggestion. */
function useLogoColor(api: ApiClient, iconAssetId: string | null | undefined): string | null {
  const [color, setColor] = useState<string | null>(null);
  useEffect(() => {
    setColor(null);
    if (!iconAssetId) return;
    let live = true;
    api
      .iconPng(iconAssetId, "light", 64)
      .then(logoColor)
      .then((c) => live && setColor(c), () => {});
    return () => {
      live = false;
    };
  }, [api, iconAssetId]);
  return color;
}

async function toBase64(f: File): Promise<string> {
  const bytes = new Uint8Array(await f.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function HeaderPreview({ mediaId }: { mediaId: string }) {
  const url = useMediaUrl(mediaId);
  return <span className="flex h-8 min-w-0 items-center">{url && <img src={url} alt="" className="h-full w-auto max-w-[200px] object-contain" />}</span>;
}

/** Description, accent color, and links for an area, space, or project; icon and header image for areas and spaces. */
export function DetailsDialog({ target, open, onOpenChange }: { target: Target; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { api, run } = useContainerOps();
  const c = target.kind === "area" ? target.area : target.kind === "space" ? target.space : target.project;
  const branded = target.kind === "project" ? null : target.kind === "area" ? target.area : target.space;
  const [description, setDescription] = useState(c.description);
  const [color, setColor] = useState(c.color ?? "");
  const [links, setLinks] = useState<ContainerLink[]>(c.links);
  const [headerMediaId, setHeaderMediaId] = useState(branded?.headerMediaId ?? null);
  const suggested = useLogoColor(api, branded?.iconAssetId);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const colorValid = color === "" || HexColor.safeParse(color).success;
  const cleanLinks = links.filter((l) => l.label.trim() || l.url.trim());
  const linksCheck = ContainerLinks.safeParse(cleanLinks);
  const badLink = (l: ContainerLink) => (l.label.trim() || l.url.trim()) && !ContainerLink.safeParse(l).success;

  const setLink = (i: number, patch: Partial<ContainerLink>) => setLinks((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const move = (i: number, d: -1 | 1) =>
    setLinks((ls) => {
      const next = [...ls];
      [next[i], next[i + d]] = [next[i + d]!, next[i]!];
      return next;
    });

  const upload = async (f: File | undefined) => {
    setError(null);
    if (!f) return;
    const mime = f.type === "image/svg+xml" || /\.svg$/i.test(f.name) ? "image/svg+xml" : f.type === "image/png" ? "image/png" : null;
    if (!mime) return setError("Choose an SVG or PNG file.");
    if (f.size > MAX_UPLOAD) return setError("That file is over 12 MB.");
    setUploading(true);
    let media: Media | undefined;
    await run("upload the image", async () => {
      media = await api.createMedia({ name: f.name.replace(/\.(svg|png)$/i, ""), mime, base64: await toBase64(f) });
    });
    setUploading(false);
    if (media) setHeaderMediaId(media.id);
  };

  const save = async () => {
    if (!colorValid || !linksCheck.success) return;
    setSaving(true);
    const patch = {
      description: description.trim(),
      color: color ? color.toUpperCase() : null,
      links: linksCheck.data,
      baseRevision: c.revision,
    };
    const ok = await run(
      "save the details",
      () =>
        target.kind === "area"
          ? api.updateArea(c.id, { ...patch, headerMediaId })
          : target.kind === "space"
            ? api.updateSpace(c.id, { ...patch, headerMediaId })
            : api.updateProject(c.id, patch),
      `Updated ${c.name}`,
      undefined,
      {
        apply: [
          {
            kind: target.kind,
            id: c.id,
            patch: { description: patch.description, color: patch.color, links: patch.links, ...(target.kind === "project" ? {} : { headerMediaId }) },
          },
        ],
      },
    );
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <DialogBody className="space-y-4">
            <DialogTitle>Details for {c.name}</DialogTitle>

            <div className="space-y-1.5">
              <Label htmlFor="details-description">Description</Label>
              <Textarea id="details-description" rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="details-color">Color</Label>
              <div className="flex flex-wrap items-center gap-1.5">
                <div role="radiogroup" aria-label="Preset colors" className="flex flex-wrap gap-1.5">
                  {suggested && (
                    <Tooltip content="From logo">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={color.toUpperCase() === suggested}
                        aria-label={`From logo (${suggested})`}
                        onClick={() => setColor(suggested)}
                        className={cn(
                          "size-6 rounded-full border border-border outline-none ring-offset-2 ring-offset-popover focus-visible:ring-2 focus-visible:ring-ring",
                          color.toUpperCase() === suggested && "ring-2 ring-foreground",
                        )}
                        style={{ background: suggested }}
                      />
                    </Tooltip>
                  )}
                  {suggested && <span aria-hidden className="mx-0.5 h-6 w-px bg-border" />}
                  {SWATCHES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      role="radio"
                      aria-checked={color.toUpperCase() === s}
                      aria-label={s}
                      title={s}
                      onClick={() => setColor(s)}
                      className={cn(
                        "size-6 rounded-full border border-border outline-none ring-offset-2 ring-offset-popover focus-visible:ring-2 focus-visible:ring-ring",
                        color.toUpperCase() === s && "ring-2 ring-foreground",
                      )}
                      style={{ background: s }}
                    />
                  ))}
                </div>
                <Input
                  id="details-color"
                  aria-invalid={!colorValid}
                  placeholder="#RRGGBB"
                  value={color}
                  maxLength={7}
                  onChange={(e) => setColor(e.target.value.trim())}
                  className={cn("h-7 w-[92px] font-mono text-[12.5px]", !colorValid && "border-destructive")}
                />
                {color && (
                  <Button size="xs" variant="quiet" onClick={() => setColor("")}>
                    None
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Links</Label>
              <ul className="space-y-1.5">
                {links.map((l, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <Input
                      aria-label={`Link ${i + 1} label`}
                      placeholder="Label"
                      value={l.label}
                      maxLength={60}
                      onChange={(e) => setLink(i, { label: e.target.value })}
                      className={cn("h-7 w-[140px] text-[13px]", badLink(l) && !l.label.trim() && "border-destructive")}
                    />
                    <Input
                      aria-label={`Link ${i + 1} URL`}
                      placeholder="https:// or file://"
                      value={l.url}
                      onChange={(e) => setLink(i, { url: e.target.value })}
                      aria-invalid={!!badLink(l)}
                      className={cn("h-7 min-w-0 flex-1 text-[13px]", badLink(l) && "border-destructive")}
                    />
                    <Button size="icon-sm" variant="quiet" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                      <ArrowUp />
                    </Button>
                    <Button size="icon-sm" variant="quiet" aria-label="Move down" disabled={i === links.length - 1} onClick={() => move(i, 1)}>
                      <ArrowDown />
                    </Button>
                    <Button size="icon-sm" variant="quiet" aria-label="Remove link" onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}>
                      <X />
                    </Button>
                  </li>
                ))}
              </ul>
              {links.length < 10 && (
                <Button size="xs" variant="quiet" onClick={() => setLinks((ls) => [...ls, { label: "", url: "" }])}>
                  <Plus />
                  Add link
                </Button>
              )}
              {!linksCheck.success && <p className="text-[12.5px] text-destructive">Each link needs a label and an http(s) or file:// URL.</p>}
            </div>

            {branded && (
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <div className="flex items-center gap-2">
                <ContainerGlyph of={branded} className="size-7" />
                <Button size="sm" variant="outline" onClick={() => setIconOpen(true)}>
                  {branded.iconAssetId || branded.icon ? "Change icon…" : "Choose icon…"}
                </Button>
              </div>
            </div>
            )}

            {branded && (
            <div className="space-y-1.5">
              <Label>Header image</Label>
              <div className="flex items-center gap-2">
                {headerMediaId && <HeaderPreview mediaId={headerMediaId} />}
                <input ref={file} type="file" accept=".svg,.png,image/svg+xml,image/png" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => file.current?.click()}>
                  <Upload />
                  {headerMediaId ? "Replace" : "Upload SVG or PNG"}
                </Button>
                {headerMediaId && (
                  <Button size="sm" variant="quiet" onClick={() => setHeaderMediaId(null)}>
                    Remove
                  </Button>
                )}
              </div>
              {error && (
                <p role="alert" className="text-[12.5px] text-destructive">
                  {error}
                </p>
              )}
            </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving || uploading || !colorValid || !linksCheck.success}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {iconOpen && target.kind !== "project" && <IconDialog target={target} open onOpenChange={setIconOpen} />}
    </Dialog>
  );
}
